import { logEvent } from "@shared/analytics/analytics";
import { toast } from "@shared/hooks/use-toast";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@shared/ui/resizable";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCanvasTemplate } from "../../hooks";
import { ComponentStyle, NormalizedComponent } from "../../types";

// Template System imports
import { MOCK_BILL_DATA } from "../../api";
import { useTemplateSystem } from "../../hooks";

// Extracted components
import CanvasArea from "../designer/CanvasArea";
import ComponentLibraryPanel from "../designer/ComponentLibraryPanel";
import ContextMenu from "../designer/ContextMenu";
import DesignerHeader from "../designer/DesignerHeader";
import { useExportManager } from "../designer/ExportManager";
import PropertyPanel from "../designer/PropertyPanel";

const PAGE_SIZES = {
  a4: { width: 794, height: 1123, label: "A4 (210×297mm)" },
  letter: { width: 816, height: 1056, label: 'Letter (8.5×11")' },
  legal: { width: 816, height: 1344, label: 'Legal (8.5×14")' },
  custom: { width: 800, height: 1000, label: "Custom" },
};

interface BillCanvasDesignerProps {
  baseTemplateId?: string;
  templateId?: string;
}

const BillCanvasDesigner: React.FC<BillCanvasDesignerProps> = ({
  baseTemplateId,
  templateId,
}) => {
  const navigate = useNavigate();

  // Template System Hook
  const {
    frontComponents: templateFrontComponents,
    backComponents: templateBackComponents,
    activeSide: templateActiveSide,
    isInitialized,
    initializeTemplate,
    addComponent: addTemplateComponent,
    updateComponent: updateTemplateComponent,
    deleteComponent: deleteTemplateComponent,
    updateField,
    toggleField,
    toggleLabel,
    reorderField,
    updateComponentContent,
    updateComponentImageUrl,
    getFields,
    renderComponent: renderTemplateComponent,
    renderPreview: renderTemplatePreview,
    switchSide: switchTemplateSide,
    exportConfiguration,
    templateType: systemTemplateType,
    setTemplateType: setSystemTemplateType,
    category: systemCategory,
    setCategory: setSystemCategory,
    perforationEnabled: systemPerfEnabled,
    setPerforationEnabled: setSystemPerfEnabled,
    perforationY: systemPerfY,
    setPerforationY: setSystemPerfY,
  } = useTemplateSystem();

  // Original hook for API
  const {
    components: hookComponents,
    availableComponents,
    templateName,
    setTemplateName,
    isLoading,
    isLoadingDetail,
    error,
    queryErrorDetail,
    saveTemplate: hookSaveTemplate,
    isSaving,
    updateTemplate: hookUpdateTemplate,
    isUpdating,
    templateId: existingTemplateId,
    baseTemplateId: hookBaseTemplateId,
    loadTemplate,
    isLoadingBaseTemplate,
    activeSide,
    setActiveSide,
    rawDetailData,
    templateType: hookTemplateType,
    setTemplateType: setHookTemplateType,
    category: hookCategory,
    setCategory: setHookCategory,
    perforationEnabled: hookPerfEnabled,
    setPerforationEnabled: setHookPerfEnabled,
    perforationY: hookPerfY,
    setPerforationY: setHookPerfY,
    isDoubleSided: hookIsDoubleSided,
    setIsDoubleSided: setHookIsDoubleSided,
  } = useCanvasTemplate(templateId);

  // Local state
  const [pageSize, setPageSize] = useState("a4");
  const [customWidth, setCustomWidth] = useState(800);
  const [customHeight, setCustomHeight] = useState(1000);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState("");
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [componentStartPos, setComponentStartPos] = useState<{
    x: number;
    y: number;
    width?: number;
    height?: number;
  }>({ x: 0, y: 0 });
  const [tool, setTool] = useState<"select" | "move">("select");
  const [activeTab, setActiveTab] = useState<"canvas" | "preview">("canvas");
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [localTemplateName, setLocalTemplateName] = useState(templateName);
  const [localTemplateType, setLocalTemplateType] = useState(hookTemplateType);
  const [localCategory, setLocalCategory] = useState(hookCategory);
  const [localPerfEnabled, setLocalPerfEnabled] = useState(hookPerfEnabled);
  const [localPerfY, setLocalPerfY] = useState(hookPerfY);
  const [isDoubleSided, setIsDoubleSided] = useState(false);
  // activeSide is now coming from useCanvasTemplate hook
  const [isSavingLocal, setIsSavingLocal] = useState(false);
  const [propertyTab, setPropertyTab] = useState<
    "properties" | "configuration"
  >("properties");
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    componentId: string | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    componentId: null,
  });

  // Force re-render when configuration changes
  const [configUpdateCounter, setConfigUpdateCounter] = useState(0);

  // Get active components from template system
  const components = isInitialized
    ? activeSide === "front"
      ? templateFrontComponents
      : templateBackComponents
    : [];

  const canvasRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);
  const gridSize = 10;
  const currentPageSize =
    pageSize === "custom"
      ? { width: customWidth, height: customHeight }
      : PAGE_SIZES[pageSize as keyof typeof PAGE_SIZES];

  // Export Manager Hook
  const { exportToPDF, exportToJPG, exportToPNG, isExporting } =
    useExportManager({
      canvasRef,
      activeSide,
      isDoubleSided,
      templateName: localTemplateName,
      pageSize: currentPageSize,
      onSideChange: setActiveSide,
    });

  // Sync local template name
  useEffect(() => {
    setLocalTemplateName(templateName);
  }, [templateName]);

  // Sync global template settings from hook to local state
  useEffect(() => {
    setLocalTemplateType(hookTemplateType);
    setSystemTemplateType(hookTemplateType);
  }, [hookTemplateType, setSystemTemplateType]);

  useEffect(() => {
    setLocalCategory(hookCategory);
    setSystemCategory(hookCategory);
  }, [hookCategory, setSystemCategory]);

  useEffect(() => {
    setLocalPerfEnabled(hookPerfEnabled);
    setSystemPerfEnabled(hookPerfEnabled);
  }, [hookPerfEnabled, setSystemPerfEnabled]);

  useEffect(() => {
    setLocalPerfY(hookPerfY);
    setSystemPerfY(hookPerfY);
  }, [hookPerfY, setSystemPerfY]);

  useEffect(() => {
    if (hookIsDoubleSided !== undefined) {
      setIsDoubleSided(hookIsDoubleSided);
    }
  }, [hookIsDoubleSided]);

  // Ensure active side is front when double-sided is turned off
  useEffect(() => {
    if (!isDoubleSided && activeSide !== "front") {
      setActiveSide("front");
    }
  }, [isDoubleSided, activeSide, setActiveSide]);

  // Initialize template system when available components arrive
  useEffect(() => {
    if (
      availableComponents &&
      availableComponents.length > 0 &&
      !isInitialized &&
      !isLoadingDetail
    ) {
      console.log("📦 Initializing template system...");

      const initializationResponse = rawDetailData
        ? {
            ...rawDetailData,
            components: availableComponents,
          }
        : {
            id: existingTemplateId || 0,
            templateName: templateName,
            components: availableComponents,
            defaultLayout:
              hookComponents.length > 0
                ? hookComponents.reduce((acc: any, comp: any) => {
                    acc[comp.id] = {
                      componentId: comp.componentId,
                      x: comp.x,
                      y: comp.y,
                      width: comp.width,
                      height: comp.height,
                      zIndex: comp.zIndex,
                      rotation: comp.rotation,
                    };
                    return acc;
                  }, {} as any)
                : {},
          };

      initializeTemplate(initializationResponse as any);
    }
  }, [
    availableComponents,
    isInitialized,
    isLoadingDetail,
    rawDetailData,
    hookComponents,
    existingTemplateId,
    templateName,
    initializeTemplate,
  ]);

  // Auto-load template 19 on mount for new templates (to populate component library)
  useEffect(() => {
    if (
      !templateId &&
      !isLoadingBaseTemplate &&
      availableComponents.length === 0
    ) {
      console.log("🚀 Auto-loading default base template for library");
      loadTemplate();
    }
  }, [templateId, isLoadingBaseTemplate, availableComponents.length]);
  // }, []);

  // Sync activeSide with template system
  useEffect(() => {
    if (isInitialized && templateActiveSide !== activeSide) {
      switchTemplateSide(activeSide);
    }
  }, [activeSide, isInitialized]);

  const snapToGridValue = (value: number) => {
    return snapToGrid ? Math.round(value / gridSize) * gridSize : value;
  };

  const handleTemplateNameChange = (name: string) => {
    setLocalTemplateName(name);
    setTemplateName(name);
  };

  const handleTemplateTypeChange = (type: string) => {
    setLocalTemplateType(type);
    setHookTemplateType(type);
    setSystemTemplateType(type);
  };

  const handleCategoryChange = (cat: string) => {
    setLocalCategory(cat);
    setHookCategory(cat);
    setSystemCategory(cat);
  };

  const handlePerfEnabledChange = (enabled: boolean) => {
    setLocalPerfEnabled(enabled);
    setHookPerfEnabled(enabled);
    setSystemPerfEnabled(enabled);
  };

  const handlePerfYChange = (y: number) => {
    setLocalPerfY(y);
    setHookPerfY(y);
    setSystemPerfY(y);
  };

  const handleDoubleSidedChange = (value: boolean) => {
    setIsDoubleSided(value);
    setHookIsDoubleSided(value);
  };

  const addComponent = (componentDef: any) => {
    if (!isInitialized) {
      console.error("❌ Template system not initialized");
      return;
    }

    console.log("➕ Adding component:", componentDef.componentName);
    const newId = addTemplateComponent(componentDef);
    if (newId) {
      setSelectedComponent(newId);
    }
  };

  const updateComponent = (
    id: string,
    updates: Partial<NormalizedComponent>
  ) => {
    if (isInitialized) {
      console.log("🔄 Updating component:", id, updates);
      updateTemplateComponent(id, updates);
    }
  };

  const updateComponentStyle = (
    id: string,
    styleUpdates: Partial<ComponentStyle>
  ) => {
    if (isInitialized) {
      updateTemplateComponent(id, { style: styleUpdates });
    }
  };

  const deleteComponent = (id: string) => {
    if (isInitialized) {
      console.log("🗑️ Deleting component:", id);
      deleteTemplateComponent(id);
    }
    if (selectedComponent === id) {
      setSelectedComponent(null);
    }
  };

  const duplicateComponent = (id: string) => {
    const component = components.find((comp) => comp.id === id);
    if (component && isInitialized) {
      const newComponent = {
        ...component,
        id: `component-${Date.now()}`,
        x: component.x + 20,
        y: component.y + 20,
        zIndex: Math.max(...components.map((c) => c.zIndex)) + 1,
      };

      const compDef = availableComponents.find(
        (c) => c.id === component.componentId
      );
      if (compDef) {
        const newId = addTemplateComponent(compDef, {
          x: component.x + 20,
          y: component.y + 20,
          width: component.width,
          height: component.height,
        });

        if (newId) {
          setSelectedComponent(newId);
        }
      }
    }
  };

  // Context Menu Handlers
  const handleContextMenu = (e: React.MouseEvent, componentId: string) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      componentId,
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, componentId: null });
  };

  const handleBringToFront = () => {
    if (contextMenu.componentId) {
      const maxZ = Math.max(...components.map((c) => c.zIndex));
      updateComponent(contextMenu.componentId, { zIndex: maxZ + 1 });
    }
    closeContextMenu();
  };

  const handleSendToBack = () => {
    if (contextMenu.componentId) {
      const minZ = Math.min(...components.map((c) => c.zIndex));
      updateComponent(contextMenu.componentId, { zIndex: minZ - 1 });
    }
    closeContextMenu();
  };

  const handleBringForward = () => {
    if (contextMenu.componentId) {
      const component = components.find(
        (c) => c.id === contextMenu.componentId
      );
      if (component) {
        updateComponent(contextMenu.componentId, {
          zIndex: component.zIndex + 1,
        });
      }
    }
    closeContextMenu();
  };

  const handleSendBackward = () => {
    if (contextMenu.componentId) {
      const component = components.find(
        (c) => c.id === contextMenu.componentId
      );
      if (component) {
        updateComponent(contextMenu.componentId, {
          zIndex: component.zIndex - 1,
        });
      }
    }
    closeContextMenu();
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setSelectedComponent(null);
    }
    closeContextMenu();
  };

  const handleComponentClick = (e: React.MouseEvent, componentId: string) => {
    e.stopPropagation();
    const component = components.find((c) => c.id === componentId);
    if (!component || component.isLocked) return;

    if (selectedComponent !== componentId) {
      setSelectedComponent(componentId);
    }
  };

  const handleMouseDown = (
    e: React.MouseEvent,
    componentId: string,
    isResize = false,
    handle = ""
  ) => {
    e.preventDefault();
    e.stopPropagation();

    const component = components.find((c) => c.id === componentId);
    if (!component || component.isLocked) return;

    setSelectedComponent(componentId);

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    if (isResize && handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStartPos({ x: mouseX, y: mouseY });
      setComponentStartPos({
        x: component.x,
        y: component.y,
        width: component.width,
        height: component.height,
      });
    } else if (tool === "move" || e.shiftKey) {
      setIsDragging(true);
      setDragStartPos({ x: mouseX, y: mouseY });
      setComponentStartPos({
        x: component.x,
        y: component.y,
        width: component.width,
        height: component.height,
      });
    }
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!selectedComponent || (!isDragging && !isResizing)) return;

      const component = components.find((c) => c.id === selectedComponent);
      if (!component) return;

      const deltaX = e.clientX - dragStartPos.x;
      const deltaY = e.clientY - dragStartPos.y;

      if (isDragging) {
        const newX = snapToGridValue(componentStartPos.x + deltaX);
        const newY = snapToGridValue(componentStartPos.y + deltaY);

        updateComponent(selectedComponent, {
          x: Math.max(
            0,
            Math.min(newX, currentPageSize.width - component.width)
          ),
          y: Math.max(
            0,
            Math.min(newY, currentPageSize.height - component.height)
          ),
        });
      } else if (
        isResizing &&
        componentStartPos.width !== undefined &&
        componentStartPos.height !== undefined
      ) {
        let newWidth = componentStartPos.width;
        let newHeight = componentStartPos.height;
        let newX = componentStartPos.x;
        let newY = componentStartPos.y;

        switch (resizeHandle) {
          case "se":
            newWidth = componentStartPos.width + deltaX;
            newHeight = componentStartPos.height + deltaY;
            break;
          case "sw":
            newWidth = componentStartPos.width - deltaX;
            newX = componentStartPos.x + deltaX;
            newHeight = componentStartPos.height + deltaY;
            break;
          case "ne":
            newWidth = componentStartPos.width + deltaX;
            newHeight = componentStartPos.height - deltaY;
            newY = componentStartPos.y + deltaY;
            break;
          case "nw":
            newWidth = componentStartPos.width - deltaX;
            newHeight = componentStartPos.height - deltaY;
            newX = componentStartPos.x + deltaX;
            newY = componentStartPos.y + deltaY;
            break;
        }

        newWidth = Math.max(50, newWidth);
        newHeight = Math.max(50, newHeight);
        newX = snapToGridValue(newX);
        newY = snapToGridValue(newY);
        newWidth = snapToGridValue(newWidth);
        newHeight = snapToGridValue(newHeight);

        updateComponent(selectedComponent, {
          x: Math.max(0, newX),
          y: Math.max(0, newY),
          width: newWidth,
          height: newHeight,
        });
      }
    },
    [
      isDragging,
      isResizing,
      selectedComponent,
      components,
      dragStartPos,
      componentStartPos,
      currentPageSize,
      snapToGrid,
      resizeHandle,
    ]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle("");
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const updatePreview = useCallback(() => {
    if (activeTab !== "preview" || !previewRef.current) return;

    const previewHtml = isInitialized
      ? renderTemplatePreview({}, currentPageSize)
      : "";

    const previewDoc = previewRef.current.contentDocument;
    if (previewDoc) {
      previewDoc.open();
      previewDoc.write(previewHtml);
      previewDoc.close();
    }
  }, [
    components,
    currentPageSize,
    activeTab,
    isInitialized,
    renderTemplatePreview,
    activeSide,
  ]);

  useEffect(() => {
    updatePreview();
  }, [updatePreview, activeTab]);

  const handleSave = async () => {
    try {
      if (!localTemplateName.trim()) {
        toast({
          title: "Please enter a template name",
          variant: "destructive",
        });
        return;
      }
      if (localTemplateType === "") {
        toast({
          title: "Please select a template type",
          variant: "destructive",
        });
        return;
      }
      if (localCategory === "") {
        toast({
          title: "Please select a category",
          variant: "destructive",
        });
        return;
      }

      setIsSavingLocal(true);

      if (isInitialized) {
        const exported = exportConfiguration();
        console.log("Exported:", exported);

        const payload: any = {
          remote_utility_id: getRemoteUtilityId(),
          configured_template_name: localTemplateName,
          template_type: localTemplateType,
          category: localCategory,
          base_template: 50,
          is_active: true,
          is_default_template: false,
          page: {
            size: pageSize.toUpperCase(),
            orientation: "portrait",
            width_mm: 210,
            height_mm: 297,
            margin: {
              top: 10,
              right: 10,
              bottom: 10,
              left: 10,
            },
          },
          pages: {
            front: {
              canvas_layout: exported.front.canvasLayout,
              component_config: exported.front.componentConfig,
            },
            ...(isDoubleSided
              ? {
                  back: {
                    canvas_layout: exported.back.canvasLayout,
                    component_config: exported.back.componentConfig,
                  },
                }
              : {}),
          },
          perforation_line: exported.perforationLine,
        };

        if (existingTemplateId) {
          await hookUpdateTemplate(payload);
          toast({ title: "Template saved successfully!" });
          navigate("/bx/bill-setup/billtemplate");
          logEvent("Template Updated", { templateType: hookBaseTemplateId });
        } else {
          await hookSaveTemplate(payload);
          logEvent("Template Created", { templateType: hookBaseTemplateId });
          toast({ title: "Template saved successfully!" });
          navigate("/bx/bill-setup/billtemplate");
        }
      }
    } catch (error: any) {
      console.error("Error saving template:", error);
      alert(error.message || "Failed to save template");
    } finally {
      setIsSavingLocal(false);
    }
  };

  const handleExport = (format: "pdf" | "jpg" | "png") => {
    if (format === "pdf") {
      exportToPDF();
    } else if (format === "jpg") {
      exportToJPG();
    } else {
      exportToPNG();
    }
  };

  const selectedComp = components.find((c) => c.id === selectedComponent);

  if (isLoading || isLoadingDetail || isLoadingBaseTemplate) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }

  // if (error || queryErrorDetail) {
  //   return (
  //     <div className="flex items-center justify-center h-screen text-red-500">
  //       Error: {error}
  //     </div>
  //   );
  // }

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <DesignerHeader
        templateName={localTemplateName}
        onTemplateNameChange={handleTemplateNameChange}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        isDoubleSided={isDoubleSided}
        onDoubleSidedChange={handleDoubleSidedChange}
        activeSide={activeSide}
        onSideChange={setActiveSide}
        tool={tool}
        onToolChange={setTool}
        showGrid={showGrid}
        onShowGridChange={setShowGrid}
        snapToGrid={snapToGrid}
        onSnapToGridChange={setSnapToGrid}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onExport={handleExport}
        onSave={handleSave}
        isSaving={isSaving || isUpdating || isSavingLocal}
        isExporting={isExporting}
        templateType={localTemplateType}
        onTemplateTypeChange={handleTemplateTypeChange}
        category={localCategory}
        onCategoryChange={handleCategoryChange}
        perforationEnabled={localPerfEnabled}
        onPerforationEnabledChange={handlePerfEnabledChange}
        perforationY={localPerfY}
        onPerforationYChange={handlePerfYChange}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          {/* Left Panel */}
          <ResizablePanel defaultSize={20} minSize={15} maxSize={30}>
            <ComponentLibraryPanel
              availableComponents={availableComponents}
              onAddComponent={addComponent}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Canvas */}
          <ResizablePanel
            defaultSize={activeTab === "canvas" && !previewExpanded ? 55 : 80}
          >
            <div className="h-full bg-gray-100 overflow-auto">
              {activeTab === "canvas" ? (
                <CanvasArea
                  components={components}
                  selectedComponent={selectedComponent}
                  pageSize={currentPageSize}
                  showGrid={showGrid}
                  tool={tool}
                  onCanvasClick={handleCanvasClick}
                  onComponentClick={handleComponentClick}
                  onComponentMouseDown={handleMouseDown}
                  onContextMenu={handleContextMenu}
                  renderComponent={renderTemplateComponent}
                  mockData={MOCK_BILL_DATA}
                  onUpdateComponent={updateComponent}
                  onDeleteComponent={deleteComponent}
                  onDuplicateComponent={duplicateComponent}
                  canvasRef={canvasRef}
                  isInitialized={isInitialized}
                  perforationEnabled={localPerfEnabled}
                  perforationY={localPerfY}
                  isDoubleSided={isDoubleSided}
                  activeSide={activeSide}
                />
              ) : (
                <div className="p-4 bg-gray-100 h-full">
                  <iframe
                    ref={previewRef}
                    className="w-full h-full border-0 rounded-lg shadow-lg bg-white"
                    title="Template Preview"
                  />
                </div>
              )}
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Right Panel - Properties / Configuration */}
          {activeTab === "canvas" && !previewExpanded && (
            <ResizablePanel defaultSize={25} minSize={20} maxSize={35}>
              <PropertyPanel
                selectedComponent={selectedComp || null}
                propertyTab={propertyTab}
                onPropertyTabChange={setPropertyTab}
                onUpdateComponent={updateComponent}
                onUpdateStyle={(compId, updates) => {
                  console.log("🎨 Style update requested:", updates);
                  updateTemplateComponent(compId, {
                    style: {
                      ...(components.find((c) => c.id === compId)?.style || {}),
                      ...updates,
                    },
                  });
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                components={components}
                getFields={getFields}
                onFieldUpdate={(compId, fieldName, updates) => {
                  console.log("🔧 Field update requested:", fieldName, updates);
                  updateField(compId, fieldName, updates);
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                onToggleField={(compId, fieldName) => {
                  console.log("👁️ Toggle field requested:", fieldName);
                  toggleField(compId, fieldName);
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                onToggleLabel={(compId, fieldName) => {
                  console.log("🏷️ Toggle label requested:", fieldName);
                  toggleLabel(compId, fieldName);
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                onReorderField={(compId, fieldName, direction) => {
                  console.log(
                    "↕️ Reorder field requested:",
                    fieldName,
                    direction
                  );
                  reorderField(compId, fieldName, direction);
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                onContentUpdate={(compId, content) => {
                  console.log("📝 Content update requested:", compId);
                  updateComponentContent(compId, content);
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                onUpdateImageUrl={(compId, imageUrl) => {
                  console.log("🖼️ Image URL update requested:", compId);
                  updateComponentImageUrl(compId, imageUrl);
                  setConfigUpdateCounter((prev) => prev + 1);
                }}
                isInitialized={isInitialized}
                configUpdateCounter={configUpdateCounter}
              />
            </ResizablePanel>
          )}
        </ResizablePanelGroup>
      </div>

      {/* Context Menu */}
      <ContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={closeContextMenu}
        onBringToFront={handleBringToFront}
        onSendToBack={handleSendToBack}
        onBringForward={handleBringForward}
        onSendBackward={handleSendBackward}
        onDelete={() => {
          if (contextMenu.componentId) {
            deleteComponent(contextMenu.componentId);
          }
          closeContextMenu();
        }}
      />
    </div>
  );
};

export default BillCanvasDesigner;
